/**
 * Routing table for the Text Notes config. Each row is one `document_config.routing`
 * rule: which route-via keys and PA response statuses it matches, the alias and
 * heading it renders, the note state it produces, and the sections it adds.
 * Built on the shared CustomTable; "Add New Rule" lives in the section header.
 */
import { useMemo } from "react";
import { Button } from "risa-oasis-ui_v2";
import CustomTable from "../../../../../components/custom-table/custom-table";
import { TableCellType } from "../../../../../components/custom-table/table";
import {
  NOTE_STATE_OPTIONS,
  NOTE_STATE_STYLE_MAP,
  ROUTE_VIA_OPTIONS,
  ROUTING_RESPONSE_STATUS_OPTIONS,
} from "./textNotesRoutingOptions";
import { RoutingRuleRow } from "./textNotesRoutingUtils";

const TABLE_NAME = "text-notes-routing-table";
const SIGN_REQUIRED_PREFIX = "routing-sign-required::";
const CAN_SIGN_PREFIX = "routing-can-transition::";
/** Tags shown per multi-select cell before the rest collapse into a "+N" chip. */
const MAX_VISIBLE_TAGS = 2;
const ALIAS_MAX_CHARS = 12;
const HEADING_MAX_CHARS = 18;

const multiSelect = (
  key: string,
  label: string,
  order: number,
  width: number,
) => ({
  key,
  label,
  order,
  width,
  sortable: false,
  type: TableCellType.MULTI_SELECT_TAG,
});

const editableText = (
  key: string,
  label: string,
  order: number,
  width: number,
) => ({
  key,
  label,
  order,
  width,
  sortable: false,
  subKey: "value",
  type: TableCellType.EDITABLE_TEXT_WITH_ICON,
});

const toggle = (key: string, label: string, order: number, width: number) => ({
  key,
  label,
  order,
  width,
  sortable: false,
  type: TableCellType.TOGGLE_SWITCH,
});

const TABLE_HEADERS = [
  multiSelect("routeVia", "Route via", 0, 15),
  multiSelect("responseStatus", "Response status", 1, 16),
  editableText("alias", "Alias", 2, 9),
  {
    key: "noteState",
    label: "Note state",
    order: 3,
    width: 10,
    sortable: false,
    type: TableCellType.PILL_SELECT,
  },
  toggle("signRequired", "Sign req.", 4, 8),
  toggle("canTransitionToSigned", "Can → signed", 5, 9),
  editableText("headingTemplate", "Heading template", 6, 15),
  multiSelect("whatWeAdd", "What we add", 7, 18),
];

interface RoutingTableProps {
  resetKey: number;
  rows: RoutingRuleRow[];
  whatWeAddOptions: { value: string; label: string }[];
  onAdd: () => void;
  onChange: (id: string, changes: Partial<RoutingRuleRow>) => void;
}

/**
 * Editable table of text-note routing rules with an Add New Rule header action.
 *
 * @param resetKey - Remount key for discard/reset
 * @param rows - Current routing rows
 * @param whatWeAddOptions - Selectable note blocks, derived from the body columns
 * @param onAdd - Appends a blank routing rule
 * @param onChange - Applies a partial update to one rule
 */
const RoutingTable = ({
  resetKey,
  rows,
  whatWeAddOptions,
  onAdd,
  onChange,
}: RoutingTableProps) => {
  const tableData = useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        routeVia: {
          values: row.routeVia,
          options: ROUTE_VIA_OPTIONS,
          placeholder: "Any route",
          maxVisibleTags: MAX_VISIBLE_TAGS,
        },
        responseStatus: {
          values: row.responseStatus,
          options: ROUTING_RESPONSE_STATUS_OPTIONS,
          placeholder: "Any status",
          maxVisibleTags: MAX_VISIBLE_TAGS,
        },
        alias: {
          value: row.alias,
          placeholder: "Add alias",
          emptyLabel: "—",
          displayMaxLength: ALIAS_MAX_CHARS,
        },
        noteState: {
          value: row.noteState,
          options: NOTE_STATE_OPTIONS,
          styleMap: NOTE_STATE_STYLE_MAP,
          ariaLabel: "Select note state",
        },
        signRequired: {
          id: `${SIGN_REQUIRED_PREFIX}${row.id}`,
          defaultChecked: row.signRequired,
        },
        canTransitionToSigned: {
          id: `${CAN_SIGN_PREFIX}${row.id}`,
          defaultChecked: row.canTransitionToSigned,
        },
        headingTemplate: {
          value: row.headingTemplate,
          placeholder: "Heading template",
          emptyLabel: "—",
          displayMaxLength: HEADING_MAX_CHARS,
        },
        whatWeAdd: {
          values: row.whatWeAdd,
          options: whatWeAddOptions,
          placeholder: "Nothing",
          maxVisibleTags: MAX_VISIBLE_TAGS,
        },
      })),
    [rows, whatWeAddOptions],
  );

  /**
   * Routes CustomTable cell edits to the matching row field.
   *
   * @param rowData - Edited row (must include id)
   * @param fieldKey - Column key that changed
   * @param newValue - New string (text / pill) or string[] (multi-select) value
   */
  const handleCellChange = (
    rowData: { id?: string },
    fieldKey: string,
    newValue: string | string[],
  ) => {
    if (!rowData?.id) return;
    const map: Record<string, keyof RoutingRuleRow> = {
      routeVia: "routeVia",
      responseStatus: "responseStatus",
      whatWeAdd: "whatWeAdd",
      alias: "alias",
      headingTemplate: "headingTemplate",
      noteState: "noteState",
    };
    const field = map[fieldKey];
    if (field) onChange(rowData.id, { [field]: newValue });
  };

  /**
   * Maps a ToggleSwitch emit back to the row id and the toggle it belongs to.
   *
   * @param data - Toggle payload with encoded name and boolean value
   */
  const handleToggleChange = (data: { name: string; value: boolean }) => {
    if (data.name.startsWith(SIGN_REQUIRED_PREFIX)) {
      onChange(data.name.slice(SIGN_REQUIRED_PREFIX.length), {
        signRequired: data.value,
      });
    } else if (data.name.startsWith(CAN_SIGN_PREFIX)) {
      onChange(data.name.slice(CAN_SIGN_PREFIX.length), {
        canTransitionToSigned: data.value,
      });
    }
  };

  return (
    <div className="flex w-full min-w-0 flex-col gap-3 rounded-lg border border-primaryGray-16 bg-white p-4">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm font-semibold text-primaryGray-1">Routing</div>
        <Button
          buttonType="primary"
          size="medium"
          disabled={false}
          onClick={onAdd}
          children="Add New Rule"
        />
      </div>
      <div className="w-full min-w-0 [&_.table-container]:w-full [&_.table]:w-full [&_table]:w-full [&_td]:box-border [&_td]:min-w-0 [&_td]:grow [&_tr]:!w-full">
        <CustomTable
          key={`${TABLE_NAME}-${resetKey}`}
          tableName={TABLE_NAME}
          tableHeaders={TABLE_HEADERS}
          tableData={tableData}
          count={tableData.length}
          totalCount={tableData.length}
          itemsPerPage={50}
          isFetching={false}
          hideSearchBar
          hidePagination
          onCellValueChange={handleCellChange}
          onChangeEmit={handleToggleChange}
        />
      </div>
    </div>
  );
};

export default RoutingTable;
